import { useContext, useEffect, useState } from 'react';
import { data } from '../bridge/client';
import { userDisplayName } from '../core/channelListDerivation';
import { dndDurations, dndEndTime } from '../core/accountSettings';
import { CustomStatusCodec } from '../core/customStatus';
import type { UserRecord } from '../core/records';
import { Icon } from '../design/icons';
import { t } from '../i18n/i18n';
import { Avatar } from './Avatar';
import { EmojiGlyph } from './features/emoji/EmojiGlyph';
import { requestIntent } from './features/settings/intent';
import { statusLabel } from './features/settings/StatusSection';
import type { SettingsSection } from './features/settings/SettingsView';
import { ShellContext } from './shell/ShellContext';
import './features/settings/settings.css';

const STATUSES = ['online', 'away', 'dnd', 'offline'] as const;

/**
 * The own-profile popover at the sidebar's foot (the Mac's profile footer, plus the webapp's status
 * menu): who you are, your custom status, the four presences one click away (with how long "No
 * molestar" lasts), the doors to Mi perfil and Configuración, and sign out last.
 */
export function ProfileMenu({
  anchor,
  me,
  presence,
  onClose,
  onSignOut,
  onOpenSettings,
}: {
  anchor: DOMRect;
  me: UserRecord;
  presence: string | null;
  onClose(): void;
  onSignOut(): void;
  onOpenSettings(section: SettingsSection | null): void;
}) {
  const shell = useContext(ShellContext);
  const [dndOpen, setDndOpen] = useState(false);
  const status = presence ?? 'offline';
  const now = Date.now();
  const cs = CustomStatusCodec.fromPropsJSON(me.customStatusJSON);
  const custom = cs && CustomStatusCodec.isActive(cs, now) ? cs : null;

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.profile-popover')) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const id = setTimeout(() => window.addEventListener('mousedown', close));
    window.addEventListener('keydown', esc);
    return () => {
      clearTimeout(id);
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  const setStatus = (value: string, end = 0) => {
    onClose();
    // Optimistic in the worker: the footer dot changes on the click; a refusal restores it.
    data.call('accountSetStatus', value, end).then(
      (ok) => !ok && shell?.toast(t('profile.error.statusUpdate'), 'error'),
      () => shell?.toast(t('profile.error.statusUpdate'), 'error'),
    );
  };

  const open = (section: SettingsSection | null) => {
    onClose();
    onOpenSettings(section);
  };

  return (
    <div className="popover profile-popover" style={{ position: 'fixed', left: anchor.left, bottom: window.innerHeight - anchor.top + 8, width: 280 }} role="menu">
      <div className="pm-head">
        <Avatar userId={me.id} name={userDisplayName(me)} size={40} version={me.lastPictureUpdate} presence={status} />
        <div className="pm-head-text">
          <span className="pm-name">{userDisplayName(me)}</span>
          <span className="pm-username">@{me.username}</span>
        </div>
      </div>
      <button
        type="button"
        className="pm-custom"
        onClick={() => {
          requestIntent('customStatus');
          open('status');
        }}
      >
        <span className="st-emoji">{custom ? <EmojiGlyph name={custom.emoji} /> : <Icon name="smilePlus" size={17} />}</span>
        <span className={`pm-custom-text${custom ? '' : ' muted'}`}>{custom ? custom.text : t('profile.customStatus.set')}</span>
        {custom ? (
          <span
            role="button"
            tabIndex={0}
            className="pm-custom-clear"
            title={t('profile.customStatus.clear')}
            aria-label={t('profile.customStatus.clear')}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              data.call('accountClearCustomStatus').then((ok) => !ok && shell?.toast(t('profile.customStatus.failed'), 'error'));
            }}
          >
            <Icon name="x" size={13} />
          </span>
        ) : null}
      </button>
      <div className="menu-sep" />
      {STATUSES.map((value) => (
        <div key={value}>
          <button
            type="button"
            className="menu-item"
            role="menuitemradio"
            aria-checked={status === value}
            aria-expanded={value === 'dnd' ? dndOpen : undefined}
            onClick={() => (value === 'dnd' ? setDndOpen((o) => !o) : setStatus(value))}
          >
            <span className={`presence st-dot ${value}`} style={{ width: 10, height: 10 }} aria-hidden />
            {statusLabel(value)}
            {value === 'dnd' ? <Icon name={dndOpen ? 'chevronUp' : 'chevronDown'} size={14} style={{ marginLeft: 'auto' }} /> : status === value ? <Icon name="check" size={14} style={{ marginLeft: 'auto' }} /> : null}
          </button>
          {value === 'dnd' && dndOpen
            ? dndDurations.map((d) => (
                <button key={d} type="button" className="menu-item" role="menuitem" style={{ paddingLeft: 34 }} onClick={() => setStatus('dnd', dndEndTime(d, Date.now()))}>
                  {t(`web.settings.dnd.${d}`)}
                </button>
              ))
            : null}
        </div>
      ))}
      {status === 'ooo' ? (
        <button type="button" className="menu-item" role="menuitemradio" aria-checked onClick={() => open('status')}>
          <span className="presence st-dot ooo" style={{ width: 10, height: 10 }} aria-hidden />
          {statusLabel('ooo')}
          <Icon name="check" size={14} style={{ marginLeft: 'auto' }} />
        </button>
      ) : null}
      <div className="menu-sep" />
      <button type="button" className="menu-item" role="menuitem" onClick={() => open('profile')}>
        <Icon name="person" size={16} />
        {t('web.settings.myProfile')}
      </button>
      <button type="button" className="menu-item" role="menuitem" onClick={() => open(window.matchMedia('(max-width: 760px)').matches ? null : 'notifications')}>
        <Icon name="gear" size={16} />
        {t('web.settings.openSettings')}
      </button>
      <div className="menu-sep" />
      <button type="button" className="menu-item danger" onClick={onSignOut}>
        <Icon name="signOut" size={16} />
        {t('profile.signOut')}
      </button>
    </div>
  );
}
